import { useInternalLink } from '@acsbe/routing';
import {
  Button,
  Content,
  Link as ExternalLink,
  LinkProps,
  fromTheme,
} from '@acsbe/ui';
import {
  ComponentProps,
  FunctionComponent,
  ReactNode,
  MouseEvent as SyntheticMouseEvent,
  forwardRef,
  useId,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link as Anchor, useHref, useLinkClickHandler } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

export { Link as ExternalLink } from '@acsbe/ui';

export const useDateFormatter = () => {
  const { i18n } = useTranslation();

  return useMemo(() => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [i18n.language]);
};

export const InternalLink: FunctionComponent<
  ComponentProps<typeof Anchor> & LinkProps
> = ({ to, replace, state, preventScrollReset, relative, ...props }) => {
  const { target } = props;
  const { href, onClick: handleClick } = useInternalLink<'a'>(to, {
    target,
    replace,
    state,
    preventScrollReset,
    relative,
  });
  return (
    <ExternalLink
      {...props}
      href={href}
      onClick={
        ((e) => {
          props.onClick?.(e);
          if (!e.defaultPrevented) {
            handleClick(e);
          }
        }) as ComponentProps<'a'>['onClick']
      }
    />
  );
};

export const InternalLinkButton: FunctionComponent<
  ComponentProps<typeof Button> & ComponentProps<typeof Anchor>
> = ({ to, replace, state, preventScrollReset, relative, ...props }) => {
  const { target } = props;
  const href = useHref(to);
  const handleClick = useLinkClickHandler(to, {
    target,
    replace,
    state,
    preventScrollReset,
    relative,
  });
  return (
    <Button
      as="a"
      variant="link"
      {...props}
      href={href}
      onClick={(e) => {
        props.onClick?.(e);
        if (!e.defaultPrevented) {
          handleClick(
            e as unknown as SyntheticMouseEvent<HTMLAnchorElement, MouseEvent>,
          );
        }
      }}
    />
  );
};

export const Subtitle: FunctionComponent<ComponentProps<typeof Content>> = (
  props,
) => <Content emphasis="high" size="xl" {...props} />;

export const Notice = styled(Content).attrs({ size: 'lg' })`
  text-align: center;
` as typeof Content;

interface FixedFieldsetProps extends ComponentProps<'div'> {
  legend: ReactNode;
}

export const FixedFieldset = forwardRef<HTMLDivElement, FixedFieldsetProps>(
  ({ legend, children, ...props }, ref) => {
    const id = useId();
    return (
      <div ref={ref} role="group" aria-labelledby={id} {...props}>
        <Content id={id} variant="element" size="lg" inline data-slot="legend">
          {legend}
        </Content>
        {children}
      </div>
    );
  },
);

export const blurBackground = css`
  background-color: color-mix(
    in srgb,
    ${fromTheme('palette.scales.base.background.app')} 90%,
    transparent
  );
  backdrop-filter: blur(21px);
`;

const spinner = keyframes`
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
`;

export const Loader = styled.div.attrs({ role: 'presentation' })<{
  size?: 'lg' | 'md' | 'sm';
}>`
  --size: ${({ size = 'lg' }) =>
    size === 'lg' ? '80px' : size === 'md' ? '40px' : '20px'};
  --stroke: ${({ size = 'lg' }) =>
    size === 'lg' ? '5px' : size === 'md' ? '5px' : '2px'};
  text-align: center;

  &:after {
    display: inline-block;
    position: relative;
    vertical-align: middle;
    border-top: var(--stroke) solid rgba(0, 0, 0, 0.2);
    border-right: var(--stroke) solid rgba(0, 0, 0, 0.2);
    border-bottom: var(--stroke) solid rgba(0, 0, 0, 0.2);
    border-left: var(--stroke) solid ${fromTheme('palette.focusColor')};
    border-radius: 50%;
    width: var(--size);
    height: var(--size);
    transform: translateZ(0);
    content: '';
    will-change: transform;
    animation: ${spinner} 0.3s infinite linear;
  }
`;
